import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { apiNameDetails } from "@/lib/namable";
import { ScoreRing } from "@/components/ScoreRing";
import { AvailabilityBadge } from "@/components/AvailabilityBadge";
import { Loader2, ShieldCheck, ShieldAlert, Shield } from "lucide-react";

const RISK = {
  Low: { icon: ShieldCheck, cls: "text-success bg-success-bg" },
  Medium: { icon: Shield, cls: "text-warning bg-warning-bg" },
  High: { icon: ShieldAlert, cls: "text-danger bg-danger-bg" },
};

export function NameDetailDialog({ result, description, onClose }) {
  const open = !!result;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    if (result) {
      setData(null);
      setLoading(true);
      apiNameDetails({ name: result.name, description: description || "", industry: "" })
        .then((d) => active && setData(d))
        .catch(() => active && setData({ error: true }))
        .finally(() => active && setLoading(false));
    }
    return () => {
      active = false;
    };
  }, [result, description]);

  const risk = data?.trademark_risk && RISK[data.trademark_risk];
  const RiskIcon = risk?.icon;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-[500px] rounded-xl p-0 overflow-hidden gap-0 max-h-[85vh] overflow-y-auto" data-testid="name-detail-dialog">
        <DialogTitle className="sr-only">{result?.name || "Name details"}</DialogTitle>
        {result && (
          <>
            <div className="brand-gradient p-6 text-white flex items-start justify-between">
              <div>
                <h2 className="font-display text-[28px] font-extrabold tracking-[-0.02em]">{result.name}</h2>
                <p className="text-white/80 text-[13px] mt-0.5">{result.domain}</p>
                <div className="mt-3">
                  <AvailabilityBadge available={result.available} extension={result.domain?.slice(result.domain.indexOf("."))} />
                </div>
              </div>
              <div className="bg-white/15 rounded-full p-1">
                <ScoreRing score={result.score ?? 0} size={48} />
              </div>
            </div>

            <div className="p-6 space-y-5">
              {loading && (
                <div className="flex items-center justify-center py-10 text-ink-subtle">
                  <Loader2 className="animate-spin mr-2" size={18} /> Crafting the brand story…
                </div>
              )}
              {data?.error && <p className="text-[14px] text-ink-muted">Couldn't load details. Please try again.</p>}
              {data && !data.error && (
                <>
                  {data.tagline && (
                    <div>
                      <p className="overline text-ink-subtle mb-1.5">Tagline</p>
                      <p className="text-[17px] font-bold text-ink">“{data.tagline}”</p>
                    </div>
                  )}
                  {data.meaning && (
                    <div>
                      <p className="overline text-ink-subtle mb-1.5">Meaning</p>
                      <p className="text-[14px] text-ink-muted leading-relaxed">{data.meaning}</p>
                    </div>
                  )}
                  {data.brand_story && (
                    <div>
                      <p className="overline text-ink-subtle mb-1.5">Brand story</p>
                      <p className="text-[14px] text-ink-muted leading-relaxed">{data.brand_story}</p>
                    </div>
                  )}
                  {data.target_audience && (
                    <div>
                      <p className="overline text-ink-subtle mb-1.5">Target audience</p>
                      <p className="text-[14px] text-ink-muted leading-relaxed">{data.target_audience}</p>
                    </div>
                  )}
                  {data.logo_concept && (
                    <div>
                      <p className="overline text-ink-subtle mb-1.5">Logo concept</p>
                      <p className="text-[14px] text-ink-muted leading-relaxed">{data.logo_concept}</p>
                    </div>
                  )}
                  {risk && (
                    <div>
                      <p className="overline text-ink-subtle mb-1.5">Trademark risk</p>
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-semibold ${risk.cls}`}>
                        {RiskIcon && <RiskIcon size={14} />} {data.trademark_risk} risk
                      </span>
                      {data.trademark_reasoning && (
                        <p className="text-[13px] text-ink-muted leading-relaxed mt-2">{data.trademark_reasoning}</p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
